/** 貸与品台帳・備品マスタ・月次人員棚卸で共通に使う問い合わせと小道具。 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useCallback } from "react";

import { useApi } from "../../lib/useApi";
import { errorText } from "./hooks";

/** 貸与品台帳。`open` で貸与中だけ、`employeeId` / `equipmentId` で絞る。 */
export function useLoans(params: {
  open?: boolean;
  employeeId?: number;
  equipmentId?: number;
  limit?: number;
  enabled?: boolean;
}) {
  const api = useApi();
  const search = new URLSearchParams({
    limit: String(params.limit ?? 200),
    ...(params.open ? { open: "true" } : {}),
    ...(params.employeeId ? { employee_id: String(params.employeeId) } : {}),
    ...(params.equipmentId ? { equipment_id: String(params.equipmentId) } : {}),
  });
  return useQuery({
    queryKey: ["hr", "loans", params],
    queryFn: () => api.loans(search),
    enabled: params.enabled ?? true,
  });
}

/** その従業員の貸与品(貸与中・返却済みの全件)。詳細画面の「貸与品」節に出す。 */
export function useEmployeeLoans(employeeId: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "employee-loans", employeeId],
    queryFn: () => api.employeeLoans(employeeId!, new URLSearchParams({ limit: "200" })),
    enabled: !!employeeId,
  });
}

export function useEquipment(params: {
  status?: string;
  category?: string;
  q?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  const api = useApi();
  const search = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
    ...(params.status ? { status: params.status } : {}),
    ...(params.category ? { category: params.category } : {}),
    ...(params.q ? { q: params.q } : {}),
  });
  return useQuery({
    queryKey: ["hr", "equipment", params],
    queryFn: () => api.equipment(search),
    enabled: params.enabled ?? true,
  });
}

/** 区分(= 貸与品の品目)と、人が指定できる状態。選択肢はサーバー側が正。 */
export function useEquipmentCategories() {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "equipment-categories"],
    queryFn: api.equipmentCategories,
    staleTime: 60_000,
  });
}

export function useMonthly(month: string, templateId?: number) {
  const api = useApi();
  const search = new URLSearchParams({
    ...(month ? { month } : {}),
    ...(templateId ? { template_id: String(templateId) } : {}),
  });
  return useQuery({
    queryKey: ["hr", "monthly", month, templateId ?? 0],
    queryFn: () => api.monthly(search),
  });
}

/** 台帳が動いたら備品・従業員・タスク・監査のキャッシュをまとめて捨てる。 */
export function useInvalidateLoans() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["hr"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  }, [qc]);
}

/** CSV / Excel を落とす。Authorization が要るので fetch して blob にする。 */
export function useDownload() {
  const api = useApi();
  return useCallback(
    async (path: string) => {
      try {
        const { blob, filename } = await api.download(path);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        void message.error(errorText(e));
      }
    },
    [api],
  );
}
